<template>
  <div class="color-legend">
    <h1>{{ title }}</h1>
    <ColorLegendContinuous
      v-if="colorScale && colorScale.type === 'INTERPOLATE'"
      id="color-legend-continuous"
      :width="width"
      :height="height"
      :padding="padding"
      :mean="mean ?? value"
      :colorScale="colorScale"
      :formatOptions="format"
      :hideMean="hideMean"
      :labelStart="labelStart"
      :labelEnd="labelEnd"
    />
    <ColorLegendCategorical
      v-else-if="colors"
      id="color-legend-categorical"
      :width="width"
      :height="height"
      :kpi="kpi"
      :colors="colors"
    />
    <p v-else>{{ errorMessage ?? t('general__text__dashboard_somethings_missing') }}</p>
  </div>
</template>
<script lang="ts" setup>
import ColorLegendContinuous from './ColorLegendContinuous.vue'
import ColorLegendCategorical from './ColorLegendCategorical.vue'
import { FormatOptions } from '@/components/AnalyticsDashboard/formatter'
import { computed } from 'vue'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import useColoredEntities from '@/composables/useColoredEntities'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

type Color = {
  color: string
  label?: LocaleString
}

type Props = {
  title: string
  width?: number
  height?: number
  padding?: number
  colors?: Color[]
  format?: FormatOptions
  value?: number
  kpi?: string
  hideMean?: boolean
  labelStart?: string
  labelEnd?: string
  errorMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  height: 64,
  width: 288,
  value: undefined,
  padding: 20,
  kpi: undefined,
  format: undefined,
  colors: undefined,
  labelStart: undefined,
  labelEnd: undefined,
  errorMessage: undefined
})
const { colorScale, mean } = storeToRefs(useColoredEntities())
const { t } = useI18n()

const colors = computed(() => {
  if (props.colors) {
    return props.colors
  }
  if (colorScale.value.type === 'STEP') {
    const colors = colorScale.value?.cutoffs.map(({ color, label }) => ({
      color,
      label
    }))
    return colors ?? []
  }
  return []
})
</script>
<style lang="scss" scoped>
.title {
  width: 200px;
}
.color-legend {
  position: fixed;
  top: 64px;
  right: 0;
  margin: 16px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
  padding: 24px;
  h1 {
    color: #1a1a1a;
    font-family: 'Inter', sans-serif;
    font-style: bold;
    line-height: 16px;
    font-weight: 500;
    font-size: 13px;
    margin: 0;
    padding: 0px 0px 16px 0px;
  }
  &__line {
    fill: none;
    stroke: #76bf8a;
    stroke-width: 3px;
  }
}
</style>
